/**
 * Ink Calculator Utility
 *
 * Calculates estimated ink usage for images based on pixel color values.
 * Lighter colors use less ink, darker colors use more ink.
 */

/**
 * Calculate ink usage percentage for an image
 * @param {ImageData} imageData - Canvas ImageData object
 * @returns {Object} Ink usage statistics
 */
export const calculateInkUsage = (imageData) => {
  const { data, width, height } = imageData;
  const totalPixels = width * height;

  let totalInkValue = 0;
  let darkPixels = 0;
  let lightPixels = 0;

  // Iterate through pixels (RGBA format: 4 values per pixel)
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Skip fully transparent pixels
    if (a === 0) continue;

    // Calculate luminance (perceived brightness)
    // Using standard luminance formula
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    // Ink usage is inverse of luminance (darker = more ink)
    // Scale to 0-100 range
    const inkValue = 100 - (luminance / 255 * 100);
    totalInkValue += inkValue;

    // Classify pixels
    if (luminance < 128) {
      darkPixels++;
    } else {
      lightPixels++;
    }
  }

  // Calculate average ink usage per pixel
  const avgInkUsage = totalInkValue / totalPixels;

  // Calculate coverage percentages
  const darkCoverage = (darkPixels / totalPixels) * 100;
  const lightCoverage = (lightPixels / totalPixels) * 100;

  return {
    avgInkUsage: Math.round(avgInkUsage * 10) / 10, // Round to 1 decimal
    darkCoverage: Math.round(darkCoverage * 10) / 10,
    lightCoverage: Math.round(lightCoverage * 10) / 10,
    totalPixels,
    darkPixels,
    lightPixels,
  };
};

/**
 * Compare ink usage between two images
 * @param {Object} beforeStats - Ink stats before optimization
 * @param {Object} afterStats - Ink stats after optimization
 * @returns {Object} Comparison results
 */
export const compareInkUsage = (beforeStats, afterStats) => {
  const inkSavings = beforeStats.avgInkUsage - afterStats.avgInkUsage;
  const inkSavingsPercent = (inkSavings / beforeStats.avgInkUsage) * 100;

  const darkReduction = beforeStats.darkCoverage - afterStats.darkCoverage;

  return {
    inkSavings: Math.round(inkSavings * 10) / 10,
    inkSavingsPercent: Math.round(inkSavingsPercent * 10) / 10,
    darkReduction: Math.round(darkReduction * 10) / 10,
    recommendation: inkSavingsPercent > 10 ? 'High savings' :
                    inkSavingsPercent > 5 ? 'Moderate savings' :
                    'Low savings',
  };
};

/**
 * Get image data from canvas or image element
 * @param {HTMLCanvasElement|HTMLImageElement} source - Image source
 * @returns {ImageData} Canvas ImageData
 */
export const getImageData = (source) => {
  // Create temporary canvas if source is an image
  let canvas = source;
  let ctx;

  if (source instanceof HTMLImageElement) {
    canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;
    ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0);
  } else {
    ctx = source.getContext('2d');
  }

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

/**
 * Calculate ink savings estimate for filter changes
 * @param {Object} filters - Current filter settings
 * @returns {Object} Estimated savings
 */
export const estimateFilterSavings = (filters) => {
  let estimatedSavings = 0;
  let reasons = [];

  // Invert colors: typically high savings for dark mode content
  if (filters.invert) {
    estimatedSavings += 40;
    reasons.push('Color inversion (dark → light)');
  }

  // Grayscale: removes color ink, uses only black
  if (filters.grayscale) {
    estimatedSavings += 20;
    reasons.push('Grayscale conversion');
  }

  // Brightness increase: lighter colors = less ink
  if (filters.brightness > 100) {
    const brightnessIncrease = filters.brightness - 100;
    const savingsFromBrightness = Math.min(brightnessIncrease / 2, 30);
    estimatedSavings += savingsFromBrightness;
    reasons.push(`Brightness +${brightnessIncrease}%`);
  }

  // Background removal: removes background ink usage
  if (filters.removeBackground) {
    estimatedSavings += 25;
    reasons.push('Background removal');
  }

  // Cap at 100%
  estimatedSavings = Math.min(estimatedSavings, 100);

  return {
    estimatedSavings: Math.round(estimatedSavings),
    reasons,
    recommendation: estimatedSavings > 50 ? 'Excellent' :
                    estimatedSavings > 30 ? 'Very good' :
                    estimatedSavings > 15 ? 'Good' :
                    'Moderate',
  };
};
